import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValueLoadable } from 'recoil';
import { actionsArticlesListQuery } from '../../api/articles/actions';
import ArticleCard from '../../components/ArticleCard';
import ArticleCardSkeleton from '../../components/ArticleCard/ArticleCardSkeleton';
import Page from '../../components/Page';
import PageHeadline from '../../components/PageHeadline';
import TermsAndConditionsPagination from './components/ActionsPagination';

export type ActionsPageLayoutProps = StandardDivProps<ActionsPageLayoutClassKey>;

export type ActionsPageLayoutClassKey = 'root' | 'content' | 'empty';

const themeSettings = { name: 'ActionsPageLayout' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<ActionsPageLayoutClassKey, {}>({
      root: {},
      content: {
        display: 'grid',
        gap: `${theme.spacing(2)}px`,
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        justifyContent: 'space-evenly',
      },
      empty: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(2),
      },
    }),
  themeSettings,
);

const ActionsPageLayout = React.forwardRef<HTMLDivElement, ActionsPageLayoutProps>(
  ({ className, classes: pClasses }, ref) => {
    const classes = useStyles({ classes: pClasses });

    const pageTitle = 'Akce';
    return (
      <Page ref={ref} className={clsx(classes.root, className)} pageTitle={pageTitle}>
        <PageHeadline>{pageTitle}</PageHeadline>
        <ArticleList />
      </Page>
    );
  },
);

ActionsPageLayout.displayName = 'ActionsPageLayout';

export default withThemeProps(themeSettings)(ActionsPageLayout);

const ArticleList: React.FC = () => {
  const classes = useStyles();
  const termsLoadable = useRecoilValueLoadable(actionsArticlesListQuery);

  if (termsLoadable.state === 'hasError') {
    return <Alert severity='error'>Error...</Alert>;
  }

  if (termsLoadable.state === 'loading') {
    return <ArticleListSkeleton />;
  }

  if (termsLoadable.contents.rows?.length === 0) {
    return (
      <div className={classes.empty}>
        <Alert severity='warning'>Váš seznam je prázdný</Alert>
      </div>
    );
  }

  return (
    <>
      <div className={classes.content}>
        {(termsLoadable.contents.rows || []).map((article, idx) => (
          <ArticleCard article={article} key={idx.toString()} routeType='actionsDetail' />
        ))}
      </div>
      <TermsAndConditionsPagination />
    </>
  );
};

ArticleList.displayName = 'ArticleList';

const ArticleListSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <div className={classes.content}>
      <ArticleCardSkeleton />
      <ArticleCardSkeleton />
      <ArticleCardSkeleton />
      <ArticleCardSkeleton />
      <ArticleCardSkeleton />
      <ArticleCardSkeleton />
    </div>
  );
};
